/**
 * ActivityFeedConfigModal - Settings for the Activity Feed widget
 *
 * Toggle switches for user events and agent actions,
 * plus title and max items settings.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { ActivityFeedWidgetConfig } from '../../types/widgets';

interface ActivityFeedConfigModalProps {
  config: ActivityFeedWidgetConfig;
  onSave: (config: ActivityFeedWidgetConfig) => void;
  onClose: () => void;
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between py-1.5 cursor-pointer">
      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
          checked ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  );
}

export function ActivityFeedConfigModal({ config, onSave, onClose }: ActivityFeedConfigModalProps) {
  const [localConfig, setLocalConfig] = useState<ActivityFeedWidgetConfig>({ ...config });

  const update = (key: keyof ActivityFeedWidgetConfig, value: unknown) => {
    setLocalConfig((prev) => ({ ...prev, [key]: value }));
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Activity Feed Settings
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5 overflow-auto max-h-[60vh]">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={localConfig.title || ''}
              onChange={(e) => update('title', e.target.value)}
              placeholder="Day Log"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          </div>

          {/* Max Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Max Items (0 = unlimited)
            </label>
            <input
              type="number"
              value={localConfig.maxItems || 0}
              onChange={(e) => update('maxItems', parseInt(e.target.value, 10) || 0)}
              min={0}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          </div>

          {/* User Events */}
          <div>
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
              User Events
            </h3>
            <div className="space-y-1">
              <Toggle
                label="Task Creations"
                checked={localConfig.showTaskCreations !== false}
                onChange={(v) => update('showTaskCreations', v)}
              />
              <Toggle
                label="Task Completions"
                checked={localConfig.showTaskCompletions !== false}
                onChange={(v) => update('showTaskCompletions', v)}
              />
              <Toggle
                label="Mood Logs"
                checked={localConfig.showMoodLogs !== false}
                onChange={(v) => update('showMoodLogs', v)}
              />
              <Toggle
                label="Journal Entries"
                checked={localConfig.showJournalEntries !== false}
                onChange={(v) => update('showJournalEntries', v)}
              />
            </div>
          </div>

          {/* Agent Actions */}
          <div>
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
              Agent Actions
            </h3>
            <div className="space-y-1">
              <Toggle
                label="Agent Task Actions"
                checked={localConfig.showAgentTaskActions !== false}
                onChange={(v) => update('showAgentTaskActions', v)}
              />
              <Toggle
                label="Agent Event Actions"
                checked={localConfig.showAgentEventActions !== false}
                onChange={(v) => update('showAgentEventActions', v)}
              />
              <Toggle
                label="Agent Habit Actions"
                checked={localConfig.showAgentHabitActions !== false}
                onChange={(v) => update('showAgentHabitActions', v)}
              />
              <Toggle
                label="Agent Comments"
                checked={localConfig.showAgentComments !== false}
                onChange={(v) => update('showAgentComments', v)}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(localConfig)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
